import React, { type FC } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'

import { isArrayNotEmpty } from '../../../utils'

import { type CybusLinkedService, type CybusService, selectServicesLinksPage, Translation } from '../../../domain'

import { Button, Hyphen } from '../common'
import { useTranslator } from '../Internationalization'
import { RedirectingResourcesTable } from '../Resources'
import { RelativeRoutePathWithId } from '../routing'
import { createHandlerWithoutPropagation } from '../utils'
import { AddOrUpdateModal } from './AddOrUpdate'

const wrapperStyle: SxProps<Theme> = { mx: -0.5, display: 'flex' }
/** On one line, like the table's other cells, which clip the links that don't fit */
const tableWrapperStyle: SxProps<Theme> = { display: 'flex', columnGap: 2 }
const buttonStyle: SxProps<Theme> = { color: 'primary.700', '&:hover': { backgroundColor: 'unset' } }
/** Reads as a link cell: one 20px text line, so it doesn't make its row taller than the table's other rows */
const tableButtonStyle: SxProps<Theme> = {
    ...buttonStyle,
    typography: 'body2',
    minHeight: 0,
    p: 0,
    border: 0,
    '&:hover': { backgroundColor: 'unset', textDecoration: 'underline' },
}

const onClick = createHandlerWithoutPropagation()
type ServiceLinksProps = Readonly<{ links: CybusService['links'], isDetailedView?: boolean }>

export const ServiceLinks: FC<ServiceLinksProps> = ({ links, isDetailedView = false }) => (
    <Box data-testid="service-links" sx={isDetailedView ? wrapperStyle : tableWrapperStyle}>
        {links.map((link, k) => (
            <Button key={k} sx={isDetailedView ? buttonStyle : tableButtonStyle} variant="primaryGhost" href={link.href} target="_blank" onClick={onClick}>
                {link.name}
            </Button>
        ))}
    </Box>
)

export const Links: FC = () => {
    const translation = useTranslator()

    return (
        <RedirectingResourcesTable<'manageServicesLinksUsecase', CybusLinkedService>
            subscriptionUsecase="manageServicesLinksUsecase"
            data={selectServicesLinksPage}
            dataTableMapper={(r) => r}
            columns={{
                id: { label: Translation.SERVICE_ID, sort: true },
                name: { label: Translation.NAME, sort: true },
                links: {
                    label: translation.formatTranslation(Translation.LINK, { count: 2 }),
                    customCellRender: (links) => (isArrayNotEmpty(links) ? <ServiceLinks links={links} /> : <Hyphen />),
                },
            }}
            redirectOnRowclick={RelativeRoutePathWithId.SERVICE}
            translations={{ emptyTable: Translation.NO_SERVICE_LINKS_AVAILABLE, paginationDataType: 'SERVICE_LINK' }}
        >
            <AddOrUpdateModal mode="CREATE" />
        </RedirectingResourcesTable>
    )
}
